import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import {
  CopyTask,
  Icon,
  NewTask,
  getTranslatedText,
  BackgroundIcon,
  AccessControl,
  isAuthorised,
  getCurrentUserHook
} from '@nile/components';
import {
  Button,
  TextField,
  Tooltip,
  ButtonGroup
} from '@superops/ui-design-system';
import { Medal, Trash } from '@styled-icons/fa-solid';
import { cloneDeep, sortBy } from 'lodash';
import { constructTaskObject } from '@nile/projects/src/Utils';
import TaskCard from '@nile/projects/src/Components/Cards/TaskCard';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage, injectIntl } from 'react-intl';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import { SVGIcon } from '@superops/super-icons';
import { Op } from 'quill-delta';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';

const OuterWrapper = styled.div`
  display: flex;
  width: 50%;

  &.mlCard {
    > span {
      width: 100%;
    }
  }
`;

const TasksHolder = styled.div`
  display: flex;
  flex-direction: column;
  /* overflow: auto; */
  &.no-tasks {
    align-items: center;
    justify-content: center;
    flex: 1 1;
  }
`;

const FormWrapper = styled.div`
  min-height: 50vh;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  img {
    width: 100px;
    height: 100px;
  }
  .text {
    font-weight: 400;
    color: #000000cc;
  }
`;

const MilestoneForm = (props) => {
  const { intl } = props;
  const [title, setTitle] = useState(props?.title || '');
  const [tasks, setTasks] = useState(props?.tasks || []);
  const [errors, setErrors] = useState(props?.errors || null);
  const [sheetActive, setSheetActive] = useState(false);

  const projectContext = useContext(ProjectContext);

  const currentUser = getCurrentUserHook();

  useEffect(() => {
    props.handleUpdate({ name: title, addTasks: tasks });
  }, [title, tasks]);

  useEffect(() => setErrors(props.errors), [props.errors]);

  const filterAttrs = (obj) => {
    const { name, ...rest } = obj;
    return rest;
  };

  const updateContext = (type, taskList) => {
    if (type === 'taskTemplateList') {
      const updatedTaskList = taskList.map((taskTemp, index) => {
        const { taskTemplateId, ...rest } = taskTemp;
        if (rest.technician) {
          rest.technician = filterAttrs(rest.technician);
        }
        return { ...rest, taskOrder: tasks.length + index + 1 };
      });
      setTasks([...tasks, ...updatedTaskList]);
    }
  };

  const context = {
    value: {
      addTaskTemplateList: [],
      removeTaskTemplateList: [],
      taskTemplateList: [],
      selectedTaskTemplates: []
    },
    updateContext: updateContext
  };

  const handleChange = (val) => {
    setErrors(null);
    setTitle(val);
  };

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };

  const addTaskHandler = (type, task) => {
    const taskObj = constructTaskObject(task);
    const updatedTasks = cloneDeep(tasks);
    updatedTasks.push({ ...taskObj, taskOrder: tasks.length + 1 });
    setTasks(updatedTasks);
    setSheetActive(!sheetActive);
  };

  const deleteTaskHandler = (ind) => {
    const updatedTasks = sortBy(tasks, 'taskOrder')
      .filter((_, i) => i !== ind)
      .map((t, i) => ({ ...t, taskOrder: i + 1 }));
    setTasks(updatedTasks);
  };

  const handleTaskUpdation = (ind, task) => {
    const taskObj = constructTaskObject(task);
    const updatedTasks = cloneDeep(tasks);
    updatedTasks[ind] = taskObj;
    setTasks(updatedTasks);
  };

  const sortedTaskList = useMemo(() => sortBy(tasks, 'taskOrder'), [tasks]);

  return (
    <FormWrapper>
      <div className="fx">
        <OuterWrapper className="mlCard">
          <div className="opacity5">
            <SVGIcon name="Milestone" size="sm" color="#323232" />
          </div>
          <div className="ml-10 milestone-title">
            <Tooltip
              content={
                <div className="tooltip">
                  {title ||
                    getTranslatedText(
                      intl,
                      'ticket.newProject.untitledMilestone'
                    )}
                </div>
              }
            >
              <TextField
                placeholder={getTranslatedText(
                  intl,
                  'project.details.actions.milestone.header'
                )}
                value={title}
                onChange={handleChange}
                error={errors?.name}
                customClassName="ellipsis"
              />
            </Tooltip>
          </div>
        </OuterWrapper>
        <OuterWrapper style={{ justifyContent: 'flex-end' }}>
          <AccessControl
            reference={projectContext.features}
            roleToCheck="task.add"
            render={
              <ButtonGroup>
                <Button
                  icon={
                    <div className="opacity5">
                      <SVGIcon name="Tasks" />
                    </div>
                  }
                >
                  <NewTask
                    addOrUpdateTechGroup={addTaskHandler}
                    state={sheetActive}
                    toggleSheetActive={toggleSheetActive}
                    name={getTranslatedText(
                      intl,
                      'ticket.newProject.createTask'
                    )}
                    hideIcon
                    isLink
                    module="PROJECT_TASK"
                    details={{
                      operation: 'CREATE',
                      projectId: props?.projectId
                    }}
                  />
                </Button>
                <AccessControl
                  reference={projectContext.features}
                  roleToCheck="taskTemplate.view"
                  render={
                    <div data-qa="createMilestoneTaskTemplate">
                      <Button
                        icon={
                          <div className="opacity5">
                            <SVGIcon name="Tasks" />
                          </div>
                        }
                      >
                        <CopyTask
                          tasksList={props?.taskTemplates?.getTaskTemplateList}
                          context={context}
                          name={getTranslatedText(
                            intl,
                            'ticket.newProject.tempTask'
                          )}
                          hideIcon
                          isLink
                        />
                      </Button>
                    </div>
                  }
                />
              </ButtonGroup>
            }
          />
        </OuterWrapper>
      </div>
      <TasksHolder className={tasks?.length > 0 ? '' : 'no-tasks'}>
        {tasks?.length > 0 ? (
          <SuperSortable
            onDragEnd={(reorderedList) => {
              const newList = reorderedList.map((t, i) => ({
                ...t,
                taskOrder: i + 1
              }));
              setTasks(newList);
            }}
            list={sortedTaskList}
          >
            {sortedTaskList.map((task, index) => {
              const enableView =
                ['6', '7'].includes(props?.project?.loggedInUserRole?.roleId) || // 6 is the role id for the project member, 7 is the role id for the project owner
                currentUser?.userId === task?.technician?.userId;
              return (
                <TaskCard
                  task={task}
                  handleTaskDeletion={deleteTaskHandler}
                  handleTaskUpdation={handleTaskUpdation}
                  index={index}
                  module="PROJECT_TASK"
                  details={{
                    operation: 'CREATE',
                    projectId: props?.projectId
                  }}
                  disableEdit={
                    !isAuthorised(projectContext.features, 'task.edit')
                  }
                  disableDelete={
                    !isAuthorised(projectContext.features, 'task.delete')
                  }
                  disableView={!enableView}
                  enableDrag={isAuthorised(
                    projectContext.features,
                    'task.edit'
                  )}
                />
              );
            })}
          </SuperSortable>
        ) : (
          <ImageContainer>
            <img
              className="pt25"
              src={`${envConfig.imageRoot}svg/EmptyTask.svg`}
              alt="Empty Tasks"
            />
            <div className="text">
              <FormattedMessage id="ticket.newProject.noTask" />
            </div>
          </ImageContainer>
        )}
      </TasksHolder>
    </FormWrapper>
  );
};

export default injectIntl(MilestoneForm);
